import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  getMonetizationFormData, 
  createMonetization, 
  getMonetizationById,
  updateMonetization,
  executeMonetization,
  uploadLoanMonetizationFile,
  downloadMonetizationPdf 
} from '../services/MonetizationService';
import { routes } from '../routes/routePaths';
import { MonetizationFormData, FormValues } from '../pages/MonetizationNew';
import { removeFileExtension, hasData, isSuccessfulResponse } from '../utils/Utils';

interface UseMonetizationFormProps {
  mode: 'create' | 'edit';
  id?: string;
  operationType?: string;
}

export const useMonetizationForm = ({ mode, id, operationType }: UseMonetizationFormProps) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const productLineId = searchParams.get('product_line_id');
  
  // Helper function to get default due date (current date + 1 year for creation mode)
  const getDefaultDueDate = () => {
    if (mode === 'create') {
      const today = new Date();
      const oneYearLater = new Date(today.getFullYear() + 1, today.getMonth(), today.getDate());
      return oneYearLater.toISOString().split('T')[0];
    }
    return new Date().toISOString().split('T')[0];
  };
  
  const [formData, setFormData] = useState<MonetizationFormData | null>(null);
  const [formValues, setFormValues] = useState<FormValues>({
    product_line_id: productLineId || '',
    issue_date: new Date().toISOString().split('T')[0],
    created_at: new Date().toISOString().split('T')[0],
    guarantee_type_id: null,
    monetization_amount: null,
    monetization_date: '',
    due_date: getDefaultDueDate(),
    creditor_id: null,
    postal_code: '',
    estimated_payment_date: new Date().toISOString().split('T')[0],
    drawer: { 
      cuit: '',
      company_name: ''
    },
    observation: ''
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  
  // New states for execution section
  const [installments, setInstallments] = useState<any[]>([]);
  const [clearing, setClearing] = useState<number | null>(null);
  const [isExecuted, setIsExecuted] = useState<boolean>(false);
  
  // Refs to track previous values for liquidation date calculation
  const prevClearingRef = useRef<number | null>(null);
  const prevDueDateRef = useRef<string>('');

  // Helper function to calculate liquidation date
  const calculatesettlementDate = (dueDate: string, clearingDays: number): string => {
    if (!dueDate || clearingDays == null) return '';
    
    try {
      // Parse date explicitly to avoid timezone issues
      const [year, month, day] = dueDate.split('-').map(Number);
      const date = new Date(year, month - 1, day); // month is 0-indexed in Date constructor
      date.setDate(date.getDate() + clearingDays);
      
      // Format as DD/MM/YYYY
      const finalDay = date.getDate().toString().padStart(2, '0');
      const finalMonth = (date.getMonth() + 1).toString().padStart(2, '0');
      const finalYear = date.getFullYear();
      
      const formattedDate = `${finalDay}/${finalMonth}/${finalYear}`;
      console.log(`Calculated liquidation date: ${dueDate} + ${clearingDays} days = ${formattedDate}`);
      
      return formattedDate;
    } catch (error) {
      console.error('Error calculating liquidation date:', error);
      return '';
    }
  };

  useEffect(() => {
    if (mode === 'create' && productLineId) {
      fetchMonetizationFormData();
    } else if (mode === 'edit' && id && operationType) {
      fetchMonetizationData();
    } else if (mode === 'create' && !productLineId) {
      setError('ID de línea de producto requerido');
      setLoading(false);
    } else if (mode === 'edit' && (!id || !operationType)) {
      setError('ID y tipo de operación requeridos');
      setLoading(false);
    }
  }, [mode, productLineId, id, operationType]);

  // Auto-calculate liquidation date only when clearing or due_date changes (not on every render)
  useEffect(() => {
    const hookClearingChanged = prevClearingRef.current !== clearing;
    const formClearingChanged = prevClearingRef.current !== formValues.clearing;
    const dueDateChanged = prevDueDateRef.current !== formValues.due_date;
    
    // Use formValues.clearing if it exists, otherwise use hook clearing
    const currentClearing = formValues.clearing !== undefined && formValues.clearing !== null 
      ? formValues.clearing 
      : clearing;
    
    console.log('useEffect liquidation calculation:', {
      hookClearingChanged,
      formClearingChanged,
      dueDateChanged,
      hookClearing: clearing,
      formClearing: formValues.clearing,
      currentClearing,
      due_date: formValues.due_date,
      current_settlement_date: formValues.settlement_date,
      prevClearing: prevClearingRef.current,
      prevDueDate: prevDueDateRef.current
    });
    
    if ((hookClearingChanged || formClearingChanged || dueDateChanged) && formValues.due_date) {
      
      let calculatedDate: string;
      
      if (currentClearing === null || currentClearing === undefined) {
        // Si clearing es null, la fecha de liquidación es igual a la fecha de vencimiento
        calculatedDate = calculatesettlementDate(formValues.due_date, 0);
      } else {
        // Si clearing tiene valor, se suman los días correspondientes
        calculatedDate = calculatesettlementDate(formValues.due_date, currentClearing);
      }
      
      console.log('Calculated date:', calculatedDate, 'Current date:', formValues.settlement_date, 'Current clearing:', currentClearing);
      
      if (calculatedDate && calculatedDate !== formValues.settlement_date) {
        console.log('SETTING settlement_date from useEffect:', calculatedDate);
        setFormValues(prev => ({ ...prev, settlement_date: calculatedDate }));
      }
    }
    
    // Update refs for next comparison - use the current clearing value
    prevClearingRef.current = currentClearing;
    prevDueDateRef.current = formValues.due_date || '';
  }, [clearing, formValues.clearing, formValues.due_date, formValues.settlement_date]);

  const fetchMonetizationFormData = async () => {
    if (!productLineId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await getMonetizationFormData(productLineId);
      if (response.success && 'data' in response) {
        setFormData(response.data);
      } else {
        setError('Error al cargar los datos del formulario');
      }
    } catch (err) {
      setError('Error al cargar los datos del formulario');
    } finally {
      setLoading(false);
    }
  };

  const fetchMonetizationData = async () => {
    if (!id || !operationType) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // First get specific monetization data
      const monetizationResponse = await getMonetizationById(operationType, id);
      if (monetizationResponse.success && 'data' in monetizationResponse) {
        const data = monetizationResponse.data;
        
        // Extract installments for loans
        if (data.loan_details && data.loan_details.installments) {
          // Ensure all numeric fields have default values
          const validatedInstallments = data.loan_details.installments.map((inst: any) => ({
            ...inst,
            amortization: inst.amortization ?? 0,
            interest: inst.interest ?? 0,
            final_balance: inst.final_balance ?? 0,
          }));
          setInstallments(validatedInstallments);
        }
        
        // Extract clearing for checks and promissory notes
        if (data.check_details && data.check_details.clearing !== undefined) {
          setClearing(data.check_details.clearing);
        } else if (data.promissory_note_details && data.promissory_note_details.clearing !== undefined) {
          setClearing(data.promissory_note_details.clearing);
        }
        
        // Extract is_executed status
        if (data.is_executed !== undefined) {
          setIsExecuted(data.is_executed);
        }
        
        // Get product_line_id from the response
        const responseProductLineId = data.application_product_line_id?.toString() || productLineId;
        
        // First get form data (metadata like creditors, types, etc) to match IDs
        let formDataOptions = null;
        if (responseProductLineId) {
          const formDataResponse = await getMonetizationFormData(responseProductLineId);
          if (formDataResponse.success && 'data' in formDataResponse) {
            formDataOptions = formDataResponse.data;
            setFormData(formDataOptions);
          }
        }
        
        // Helper function to find ID by matching object properties
        const findIdByMatch = (options: any[], objectData: any, matchKey: string = 'type') => {
          if (!options || !objectData) return null;
          const match = options.find(opt => opt[matchKey] === objectData[matchKey]);
          return match ? match.id : null;
        };
        
        // Helper function to find ID by matching string value
        const findIdByStringMatch = (options: any[], value: string, matchKey: string = 'name') => {
          if (!options || !value) return null;
          const match = options.find(opt => opt[matchKey] === value);
          return match ? match.id : null;
        };
        
        // Extract IDs from response objects or strings
        const guarantee_type_id = data.guarantee_type 
          ? findIdByMatch(formDataOptions?.guarantee_types, data.guarantee_type, 'type')
          : data.guarantee_type_id || null;
          
        const creditor_id = data.creditor
          ? findIdByMatch(formDataOptions?.creditors, data.creditor, 'cuit')
          : data.creditor_name
            ? findIdByStringMatch(formDataOptions?.creditors, data.creditor_name, 'name')
            : data.creditor_id || null;
          
        const amortization_type_id = data.loan_details?.amortization_type
          ? (typeof data.loan_details.amortization_type === 'string'
              ? findIdByStringMatch(formDataOptions?.amortization_types, data.loan_details.amortization_type, 'name')
              : findIdByMatch(formDataOptions?.amortization_types, data.loan_details.amortization_type, 'name'))
          : data.loan_details?.amortization_type_id || data.amortization_type_id || null;
          
        const payment_frequency_type_id = data.loan_details?.payment_frequency_type
          ? (typeof data.loan_details.payment_frequency_type === 'string'
              ? findIdByStringMatch(formDataOptions?.payment_frequency_types, data.loan_details.payment_frequency_type, 'name')
              : findIdByMatch(formDataOptions?.payment_frequency_types, data.loan_details.payment_frequency_type, 'name'))
          : data.loan_details?.payment_frequency_type_id || data.payment_frequency_type_id || null;
          
        const rate_type_id = data.loan_details?.rate_type
          ? (typeof data.loan_details.rate_type === 'string'
              ? findIdByStringMatch(formDataOptions?.rate_types, data.loan_details.rate_type, 'name')
              : findIdByMatch(formDataOptions?.rate_types, data.loan_details.rate_type, 'name'))
          : data.loan_details?.rate_type_id || data.rate_type_id || null;
          
        const purpose_type_id = data.loan_details?.purpose_type_name
          ? findIdByStringMatch(formDataOptions?.purpose_types, data.loan_details.purpose_type_name, 'name')
          : data.commercial_endorsement_details?.purpose_type_name
            ? findIdByStringMatch(formDataOptions?.purpose_types, data.commercial_endorsement_details.purpose_type_name, 'name')
            : data.commercial_endorsement_details?.purpose_type_id || data.loan_details?.purpose_type_id || data.purpose_type_id || null;
          
        const check_format_type_id = data.check_details?.check_format_type
          ? (typeof data.check_details.check_format_type === 'string'
              ? findIdByStringMatch(formDataOptions?.check_format_types, data.check_details.check_format_type, 'name')
              : findIdByMatch(formDataOptions?.check_format_types, data.check_details.check_format_type, 'name'))
          : data.check_details?.check_format_type_id || data.check_format_type_id || null;
          
        const banking_entity_id = data.check_details?.banking_entity
          ? (typeof data.check_details.banking_entity === 'string'
              ? findIdByStringMatch(formDataOptions?.banking_entities, data.check_details.banking_entity, 'name')
              : findIdByMatch(formDataOptions?.banking_entities, data.check_details.banking_entity, 'name'))
          : data.check_details?.banking_entity_id || data.banking_entity_id || null;

        // Map the response data to form values
        const mappedFormValues = {
          product_line_id: responseProductLineId || '',
          issue_date: data.issue_date || new Date().toISOString().split('T')[0],
          created_at: data.created_at || new Date().toISOString().split('T')[0],
          guarantee_type_id: guarantee_type_id,
          monetization_amount: data.check_details?.monetization_amount || 
                              data.promissory_note_details?.monetization_amount || 
                              data.loan_details?.monetization_amount || 
                              data.monetization_amount || null,
          monetization_date: data.monetization_date || '',
          due_date: data.due_date || new Date().toISOString().split('T')[0],
          creditor_id: creditor_id,
          
          // Loan fields
          bank_operation_number: data.loan_details?.bank_operation_number || data.bank_operation_number || '',
          amortization_type_id: amortization_type_id,
          payment_frequency_type_id: payment_frequency_type_id,
          term_in_days: data.commercial_endorsement_details?.term_in_days || data.loan_details?.term_in_days || data.term_in_days || null,
          rate_type_id: rate_type_id,
          percentage_point: data.loan_details?.percentage_point || data.percentage_point || null,
          grace_period: data.loan_details?.grace_period || data.grace_period || null,
          purpose_type_id: purpose_type_id,
          
          // Check fields
          bank_branch: data.check_details?.bank_branch || data.bank_branch || '',
          check_format_type_id: check_format_type_id,
          check_number: data.check_details?.check_number || data.check_number || '',
          echeck_number: data.check_details?.echeck_number || data.echeck_number || '',
          cmc7: data.check_details?.cmc7 || data.cmc7 || '',
          estimated_payment_date: data.check_details?.estimated_payment_date || data.estimated_payment_date || new Date().toISOString().split('T')[0],
          banking_entity_id: banking_entity_id,
          banking_entity_name: data.check_details?.banking_entity_name || data.promissory_note_details?.banking_entity_name || '',
          postal_code: data.check_details?.postal_code || data.postal_code || '',
          drawer: (() => {
            const drawerData = data.check_details?.drawer || data.drawer;
            if (!drawerData) return { cuit: '', company_name: '' };
            // Normalizar: si viene 'name', usarlo como 'company_name'
            return {
              cuit: drawerData.cuit || '',
              company_name: drawerData.company_name || drawerData.name || ''
            };
          })(),
          clearing: data.check_details?.clearing !== undefined ? data.check_details.clearing : 
                   data.promissory_note_details?.clearing !== undefined ? data.promissory_note_details.clearing : null,
          
          // Promissory Note fields
          endorsed: data.promissory_note_details?.endorsed || data.endorsed || false,
          e_promissory_note_id: data.promissory_note_details?.epromissory_note_id || data.promissory_note_details?.e_promissory_note_id || data.e_promissory_note_id || '',
          
          observation: data.commercial_endorsement_details?.observation || data.observation || data.loan_details?.observation || data.check_details?.observation || data.promissory_note_details?.observation || '',
          
          // Execution fields (if present) - prioritize check_details and promissory_note_details
          monetization_cvba_code: data.check_details?.monetization_cvba_code || 
                                 data.promissory_note_details?.monetization_cvba_code || 
                                 data.monetization_cvba_code || '',
          iva: data.check_details?.iva !== undefined ? data.check_details.iva :
               data.promissory_note_details?.iva !== undefined ? data.promissory_note_details.iva :
               data.iva || false,
          monetization_discount_rate: data.check_details?.monetization_discount_rate !== undefined ? data.check_details.monetization_discount_rate :
                                     data.promissory_note_details?.monetization_discount_rate !== undefined ? data.promissory_note_details.monetization_discount_rate :
                                     data.monetization_discount_rate || null,
          monetization_interest: data.check_details?.monetization_interest !== undefined ? data.check_details.monetization_interest :
                                data.promissory_note_details?.monetization_interest !== undefined ? data.promissory_note_details.monetization_interest :
                                data.monetization_interest || null,
          settlement_date: data.settlement_date || '',
          buyer_alyc: data.check_details?.buyer_alyc || 
                     data.promissory_note_details?.buyer_alyc || 
                     data.buyer_alyc || '',
          seller_alyc: data.check_details?.seller_alyc || 
                      data.promissory_note_details?.seller_alyc || 
                      data.seller_alyc || '',
          bank_contract_file: data.file_name || data.loan_details?.file_name || '',
          id_global: data.id_global || data.idGlobal || null,
          pending_file: null
        };

        // Calculate liquidation date on initial load if not present in data
        if (mappedFormValues.due_date && !mappedFormValues.settlement_date) {
          let calculatedDate: string;
          
          if (clearing === null || clearing === undefined) {
            // Si clearing es null, la fecha de liquidación es igual a la fecha de vencimiento
            calculatedDate = calculatesettlementDate(mappedFormValues.due_date, 0);
          } else {
            // Si clearing tiene valor, se suman los días correspondientes
            calculatedDate = calculatesettlementDate(mappedFormValues.due_date, clearing);
          }
          
          if (calculatedDate) {
            console.log('Auto-calculating settlement_date on initial load:', calculatedDate, 'Clearing:', clearing);
            mappedFormValues.settlement_date = calculatedDate;
          }
        }
        
        setFormValues(mappedFormValues);
        
        // Initialize refs with loaded values
        prevClearingRef.current = clearing;
        prevDueDateRef.current = mappedFormValues.due_date || '';
        
      } else {
        setError('Error al cargar los datos de la monetización');
      }
    } catch (err) {
      setError('Error al cargar los datos de la monetización');
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    const fromParam = searchParams.get('from');
    
    // If it's edit mode, we want to go back to the "En gestión" tab in monetization
    if (mode === 'edit') {
      navigate('/monetizacion?tab=1'); // tab=1 is "En gestión"
    } else if (fromParam === 'monetization') {
      navigate(routes.monetization);
    } else if (formData && formValues) {
      const cuit = formData.cuit;
      const productLineId = formValues.product_line_id;
      navigate(`/avales/${cuit}/linea/${productLineId}`);
    } else {
      navigate(-1);
    }
  };

  // Upload loan monetization file
  const uploadLoanFile = async (): Promise<string | null> => {
    if (!formValues.pending_file || !formValues.id_global) {
      return null;
    }

    try {
      const fileName = removeFileExtension(formValues.pending_file.name);
      const formData = new FormData();
      formData.append('file', formValues.pending_file);
      formData.append('fileName', fileName);

      const response = await uploadLoanMonetizationFile(formValues.id_global, formData);
      
      if (hasData(response) && isSuccessfulResponse(response)) {
        // The response.data may be an object {message, file_name} or just a string
        const uploadedFileName = typeof response.data === 'object' && response.data?.file_name 
          ? response.data.file_name 
          : response.data;
          
        // Update form values with the returned file name
        setFormValues(prev => ({
          ...prev,
          bank_contract_file: uploadedFileName,
          pending_file: null
        }));
        return uploadedFileName;
      } else {
        setError('Error al subir el archivo del contrato bancario');
        return null;
      }
    } catch (err) {
      setError('Error al subir el archivo del contrato bancario');
      return null;
    }
  };

  const handleSubmit = async () => {
    if (!formData || !formValues) return;
    
    setValidationError(null);
    
    // Validate dates - solo si ambas fechas están presentes
    if (formValues.due_date && formValues.monetization_date) {
      const dueDate = new Date(formValues.due_date);
      const monetizationDate = new Date(formValues.monetization_date);
      
      if (dueDate < monetizationDate) {
        setValidationError('La fecha de vencimiento debe ser igual o mayor a la fecha de monetización');
        setTimeout(() => setValidationError(null), 5000);
        return;
      }
    }
    
    setSubmitting(true);
    setError(null);
    
    try {
      const operationType = getOperationType(formData.product_line_label);
      const payload = buildPayload(operationType);
      
      let response;
      if (mode === 'create') {
        response = await createMonetization(operationType, payload);
      } else {
        response = await updateMonetization(operationType, id!, payload);
      }
      
      if (response.success) {
        console.log('Response after create:', response);
        console.log('Response.data:', 'data' in response ? response.data : 'no data');
        console.log('Response.data keys:', 'data' in response && response.data ? Object.keys(response.data) : 'no keys');
        
        // Si es un préstamo recién creado (pero no operación pública), descargar el PDF antes de navegar
        if (mode === 'create' && 
            (operationType === 'préstamo' || operationType === 'prestamo') && 
            formData.product_line_label !== 'Operaciones Públicas' &&
            'data' in response) {
          // Try both camelCase and snake_case
          const idGlobal = response.data?.idGlobal || response.data?.id_global;
          console.log('ID Global found:', idGlobal, 'from data:', response.data);
          
          if (idGlobal) {
            console.log('Attempting to download PDF for idGlobal:', idGlobal);
            try {
              const pdfResponse = await downloadMonetizationPdf(idGlobal);
              console.log('PDF response:', pdfResponse);
              
              if (pdfResponse.success && 'data' in pdfResponse && pdfResponse.data) {
                const blob = new Blob([pdfResponse.data], { type: 'application/pdf' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `prestamo_${idGlobal}.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                console.log('PDF downloaded successfully');
              } else {
                console.log('PDF response not successful or no data');
              }
            } catch (pdfError) {
              console.error('Error descargando PDF:', pdfError);
              // No detenemos el flujo si falla la descarga del PDF
            }
          } else {
            console.log('No idGlobal found in response.data');
          }
        } else {
          console.log('Not downloading PDF - conditions not met', {
            mode,
            operationType,
            hasData: 'data' in response
          });
        }
        
        const cuit = formData.cuit;
        const productLineId = formValues.product_line_id;
        navigate(`/avales/${cuit}/linea/${productLineId}`);
      } else {
        setError(`Error al ${mode === 'create' ? 'crear' : 'actualizar'} la monetización`);
      }
    } catch (err) {
      setError(`Error al ${mode === 'create' ? 'crear' : 'actualizar'} la monetización`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSave = async (): Promise<boolean> => {
    if (!formData || !formValues || !id) return false;
    
    setValidationError(null);
    
    // Validate dates
    if (formValues.due_date && formValues.monetization_date) {
      const dueDate = new Date(formValues.due_date);
      const monetizationDate = new Date(formValues.monetization_date);
      
      if (dueDate < monetizationDate) {
        setValidationError('La fecha de vencimiento debe ser igual o mayor a la fecha de monetización');
        setTimeout(() => setValidationError(null), 5000);
        return false;
      }
    }
    
    setSubmitting(true);
    setError(null);
    
    try {
      // Upload file if there's a pending file (for loans)
      const operationType = getOperationType(formData.product_line_label);
      if ((operationType === 'préstamo' || operationType === 'prestamo') && formValues.pending_file) {
        const uploadedFileName = await uploadLoanFile();
        if (!uploadedFileName) {
          return false; // Upload failed, abort save
        }
      }
      
      const payload = buildUpdatePayload(operationType);
      
      const response = await updateMonetization(operationType, id, payload);
      
      if (response.success) {
        setError(null);
        // Show success message or stay on the page
        console.log('Monetización actualizada exitosamente');
        return true;
      } else {
        setError('Error al guardar los cambios');
        return false;
      }
    } catch (err) {
      setError('Error al guardar los cambios');
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const handleExecute = async (): Promise<boolean> => {
    if (!formData || !formValues || !id) return false;
    
    setSubmitting(true);
    setError(null);
    
    try {
      const operationType = getOperationType(formData.product_line_label);
      
      // Upload file if there's a pending file (for loans, but not for public operations)
      if ((operationType === 'préstamo' || operationType === 'prestamo') && 
          formData.product_line_label !== 'Operaciones Públicas' && 
          formValues.pending_file) {
        const uploadedFileName = await uploadLoanFile();
        if (!uploadedFileName) {
          return false; // Upload failed, abort execution
        }
      }
      
      let payload: any = {};
      
      // Build payload based on operation type
      if (operationType === 'cheque') {
        // For checks - all fields are required
        payload = {
          monetization_date: formValues.monetization_date,
          iva: formValues.iva,
          monetization_discount_rate: formValues.monetization_discount_rate,
          monetization_interest: formValues.monetization_interest,
          clearing: formValues.clearing,
          monetization_cvba_code: formValues.monetization_cvba_code,
          buyer_alyc: formValues.buyer_alyc,
          seller_alyc: formValues.seller_alyc
        };
      } else if (operationType === 'pagaré' || operationType === 'pagare') {
        // For promissory notes - all fields are required
        payload = {
          monetization_date: formValues.monetization_date,
          iva: formValues.iva,
          monetization_discount_rate: formValues.monetization_discount_rate,
          monetization_interest: formValues.monetization_interest,
          clearing: formValues.clearing,
          monetization_cvba_code: formValues.monetization_cvba_code,
          buyer_alyc: formValues.buyer_alyc,
          seller_alyc: formValues.seller_alyc
        };
      } else if (operationType === 'préstamo' || operationType === 'prestamo') {
        // For loans - include monetization_date if present
        if (formValues.monetization_date) {
          payload = {
            monetization_date: formValues.monetization_date
          };
        } else {
          payload = {};
        }
      } else if (operationType === 'commercial-endorsements') {
        // For commercial endorsements - all fields are required except cvba code
        payload = {
          monetization_date: formValues.monetization_date,
          iva: formValues.iva,
          monetization_discount_rate: formValues.monetization_discount_rate,
          monetization_interest: formValues.monetization_interest,
          clearing: formValues.clearing,
          buyer_alyc: formValues.buyer_alyc,
          seller_alyc: formValues.seller_alyc,
          monetization_amount: formValues.monetization_amount
        };
      }
      
      const response = await executeMonetization(operationType, id, payload);
      
      if (response.success) {
        // Redirect back to monetization list
        const cuit = formData.cuit;
        const productLineId = formValues.product_line_id;
        navigate(`/avales/${cuit}/linea/${productLineId}`);
        return true;
      } else {
        setError('Error al ejecutar la monetización');
        return false;
      }
    } catch (err) {
      setError('Error al ejecutar la monetización');
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const getOperationType = (productLineLabel: string) => {
    const label = productLineLabel.toLowerCase();
    
    if (label === 'cdp propios' || label === 'cdp terceros') {
      return 'cheque';
    }
    
    if (label === 'pagaré dólar link' || label === 'pagaré en pesos') {
      return 'pagaré';
    }
    
    if (label === 'aval comercial') {
      return 'commercial-endorsements';
    }
    
    if (label === 'operaciones públicas') {
      return 'préstamo';
    }
    
    return label;
  };

  const buildPayload = (operationType: string) => {
    let payload: any = {
      application_product_line_id: parseInt(formValues.product_line_id),
      issue_date: formValues.issue_date,
      guarantee_type_id: formValues.guarantee_type_id,
      monetization_amount: formValues.monetization_amount,
      due_date: formValues.due_date,
      creditor_id: formValues.creditor_id,
      observation: formValues.observation || '',
    };

    if (operationType === 'préstamo' || operationType === 'prestamo') {
      payload = {
        ...payload,
        bank_operation_number: formValues.bank_operation_number || '',
        amortization_type_id: formValues.amortization_type_id,
        payment_frequency_type_id: formValues.payment_frequency_type_id,
        term_in_days: formValues.term_in_days,
        rate_type_id: formValues.rate_type_id,
        percentage_point: formValues.percentage_point,
        grace_period: formValues.grace_period,
        purpose_type_id: formValues.purpose_type_id
      };
    } else if (operationType === 'cheque') {
      payload = {
        ...payload,
        bank_branch: formValues.bank_branch || '',
        banking_entity_id: formValues.banking_entity_id,
        check_format_type: formValues.check_format_type_id,
        check_number: formValues.check_number || '',
        echeck_number: formValues.echeck_number || '',
        cmc7: formValues.cmc7 || '',
        estimated_payment_date: formValues.estimated_payment_date || '',
        drawer: formValues.drawer || { cuit: '' },
        postal_code: formValues.postal_code || '',
        clearing: formValues.clearing || 0
      };
    } else if (operationType === 'pagaré' || operationType === 'pagare') {
      payload = {
        ...payload,
        banking_entity_id: formValues.banking_entity_id,
        term_in_days: formValues.term_in_days,
        endorsed: formValues.endorsed || false,
        e_promissory_note_id: formValues.e_promissory_note_id || '',
        estimated_payment_date: formValues.estimated_payment_date,
        drawer: formValues.drawer || { cuit: '' }
      };
    } else if (operationType === 'commercial-endorsements') {
      payload = {
        ...payload,
        purpose_type_id: formValues.purpose_type_id,
        term_in_days: formValues.term_in_days
      };
    }

    return payload;
  };

  const buildUpdatePayload = (operationType: string) => {
    // Base payload fields common to all operation types
    let payload: any = {
      applicationProductLineId: parseInt(formValues.product_line_id),
      issueDate: formValues.issue_date,
      guaranteeTypeId: formValues.guarantee_type_id,
      monetizationAmount: formValues.monetization_amount,
      dueDate: formValues.due_date,
      creditorId: formValues.creditor_id,
      observation: formValues.observation || '',
      // Add execution fields that are common to all types
      monetizationCvbaCode: formValues.monetization_cvba_code || '',
      iva: formValues.iva || false,
      monetizationDiscountRate: formValues.monetization_discount_rate || null,
      monetizationInterest: formValues.monetization_interest || null,
      settlementDate: formValues.settlement_date || '',
      buyerAlyc: formValues.buyer_alyc || '',
      sellerAlyc: formValues.seller_alyc || '',
      bankContractFile: formValues.bank_contract_file || ''
    };

    // Only include monetizationDate if it has a value
    if (formValues.monetization_date) {
      payload.monetizationDate = formValues.monetization_date;
    }

    // Add specific fields based on operation type
    if (operationType === 'préstamo' || operationType === 'prestamo') {
      payload = {
        ...payload,
        bankOperationNumber: formValues.bank_operation_number || '',
        amortizationTypeId: formValues.amortization_type_id,
        paymentFrequencyTypeId: formValues.payment_frequency_type_id,
        termInDays: formValues.term_in_days,
        rateTypeId: formValues.rate_type_id,
        percentagePoint: formValues.percentage_point,
        gracePeriod: formValues.grace_period,
        purposeTypeId: formValues.purpose_type_id
      };
    } else if (operationType === 'cheque') {
      payload = {
        ...payload,
        bankBranch: formValues.bank_branch || '',
        bankingEntityId: formValues.banking_entity_id,
        checkFormatType: formValues.check_format_type_id,
        checkNumber: formValues.check_number || '',
        echeckNumber: formValues.echeck_number || '',
        cmc7: formValues.cmc7 || '',
        estimatedPaymentDate: formValues.estimated_payment_date || '',
        termInDays: formValues.term_in_days,
        drawer: {
          cuit: formValues.drawer?.cuit || '',
          name: formValues.drawer?.company_name || ''
        },
        postalCode: formValues.postal_code || '',
        clearing: formValues.clearing || null
      };
    } else if (operationType === 'pagaré' || operationType === 'pagare') {
      payload = {
        ...payload,
        bankingEntityId: formValues.banking_entity_id,
        termInDays: formValues.term_in_days,
        endorsed: formValues.endorsed || false,
        ePromissoryNoteId: formValues.e_promissory_note_id || '',
        estimatedPaymentDate: formValues.estimated_payment_date,
        drawer: {
          cuit: formValues.drawer?.cuit || '',
          name: formValues.drawer?.company_name || ''
        },
        clearing: formValues.clearing || null
      };
    } else if (operationType === 'commercial-endorsements') {
      payload = {
        ...payload,
        purposeTypeId: formValues.purpose_type_id,
        termInDays: formValues.term_in_days
      };
    }

    return payload;
  };

  const updateFormValues = (updates: Partial<FormValues>) => {
    console.log('updateFormValues called with:', updates);
    if ('settlement_date' in updates) {
      console.log('settlement_date being updated to:', updates.settlement_date);
    }
    setFormValues(prev => {
      const newValues = { ...prev, ...updates };
      console.log('Previous settlement_date:', prev.settlement_date, 'New settlement_date:', newValues.settlement_date);
      return newValues;
    });
  };

  const isFormValid = () => {
    const requiredFields = [
      formValues.issue_date,
      formValues.monetization_amount,
      formValues.due_date,
      formValues.guarantee_type_id
    ];
    
    // Solo validar monetization_date en modo edición
    if (mode === 'edit' && !formValues.monetization_date) {
      return false;
    }
    
    if (!requiredFields.every(field => field)) {
      return false;
    }
    
    if (formData?.product_line_label === 'Préstamo' || formData?.product_line_label === 'Operaciones Públicas') {
      return !!(formValues.creditor_id && formValues.amortization_type_id && formValues.payment_frequency_type_id && 
               formValues.rate_type_id && formValues.percentage_point && 
               formValues.grace_period !== null && formValues.grace_period !== undefined &&
               formValues.purpose_type_id);
    }
    
    if (formData?.product_line_label === 'CDP Propios' || 
        formData?.product_line_label === 'CDP Terceros' ||
        formData?.product_line_label === 'Cheque') {
      return !!(formValues.creditor_id && formValues.bank_branch && formValues.check_format_type_id && 
               formValues.check_number && formValues.echeck_number && formValues.cmc7 && 
               formValues.banking_entity_id && formValues.drawer?.cuit && formValues.drawer?.cuit.length === 11 &&
               formValues.drawer?.company_name && formValues.postal_code && 
               formValues.clearing !== null && formValues.clearing !== undefined);
    }
    
    if (formData?.product_line_label === 'Pagaré Dólar Link' || 
        formData?.product_line_label === 'Pagaré en Pesos' ||
        formData?.product_line_label === 'Pagaré') {
      return !!(formValues.e_promissory_note_id && formValues.banking_entity_id && 
               formValues.drawer?.cuit && formValues.drawer?.cuit.length === 11 &&
               formValues.drawer?.company_name);
    }
    
    if (formData?.product_line_label === 'Aval Comercial') {
      return !!(formValues.creditor_id && formValues.purpose_type_id && 
               formValues.term_in_days !== null && formValues.term_in_days !== undefined);
    }
    
    return true;
  };

  const isExecuteValid = () => {
    if (mode !== 'edit') return false;
    
    const operationType = formData?.product_line_label?.toLowerCase();
    
    // For loans and operaciones públicas, no additional fields are required for execution
    if (operationType === 'préstamo' || operationType === 'operaciones públicas') {
      return true;
    }
    
    // For checks and promissory notes, all execution fields are required
    if (operationType === 'cdp propios' || operationType === 'cdp terceros' || 
        operationType === 'cheque' || operationType === 'pagaré dólar link' || 
        operationType === 'pagaré en pesos' || operationType === 'pagaré') {
      return !!(formValues.monetization_date && 
               formValues.iva !== null && formValues.iva !== undefined &&
               formValues.monetization_discount_rate && 
               formValues.monetization_interest && 
               formValues.clearing !== null && formValues.clearing !== undefined &&
               formValues.monetization_cvba_code && 
               formValues.buyer_alyc && 
               formValues.seller_alyc);
    }
    
    // For commercial endorsements, all execution fields are required
    if (operationType === 'aval comercial') {
      return !!(formValues.monetization_date && 
               formValues.iva !== null && formValues.iva !== undefined &&
               formValues.monetization_discount_rate && 
               formValues.monetization_interest && 
               formValues.clearing !== null && formValues.clearing !== undefined &&
               formValues.buyer_alyc && 
               formValues.seller_alyc &&
               formValues.monetization_amount);
    }
    
    return false;
  };

  return {
    formData,
    formValues,
    loading,
    submitting,
    error,
    validationError,
    updateFormValues,
    handleGoBack,
    handleSubmit,
    handleSave,
    handleExecute,
    isFormValid,
    isExecuteValid,
    installments,
    clearing,
    isExecuted,
  };
};
